import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../lib/auth-context';
import { userAPI, walletAPI, commissionPackagesAPI } from '../lib/api';
import api from '../lib/api';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from '../components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import {
  Users as UsersIcon, UserPlus, MoreVertical, Edit, Trash2, Wallet,
  Loader2, Search, Filter, Ban, CheckCircle, Eye, Lock, Copy, MapPin, Store, RefreshCw, Package,
} from 'lucide-react';
import { format } from 'date-fns';
import { useSearchParams } from 'react-router-dom';

const UsersPage = () => {
  const { user: currentUser } = useAuth();
  const [searchParams] = useSearchParams();
  const [users, setUsers] = useState([]);
  const [distributors, setDistributors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState(searchParams.get('role') || 'all');

  // Create user
  const [createOpen, setCreateOpen] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [newUser, setNewUser] = useState({ name: '', email: '', phone: '', password: '', role: '', parent_id: '', shop_name: '', address: '', postal_code: '', city: '', state: '' });
  const [pinLoading, setPinLoading] = useState(false);

  // Edit user
  const [editOpen, setEditOpen] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [editUser, setEditUser] = useState(null);

  // View detail
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailUser, setDetailUser] = useState(null);

  // Add balance
  const [addBalanceOpen, setAddBalanceOpen] = useState(false);
  const [addBalanceUser, setAddBalanceUser] = useState(null);
  const [addBalanceAmount, setAddBalanceAmount] = useState('');
  const [addBalanceLoading, setAddBalanceLoading] = useState(false);

  // Commission packages
  const [allPackages, setAllPackages] = useState([]);

  useEffect(() => { fetchUsers(); fetchAllPackages(); }, [roleFilter]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const role = roleFilter === 'all' ? undefined : roleFilter;
      const response = await userAPI.getUsers(role);
      setUsers(response.data);
      if (currentUser?.role === 'admin') {
        const distRes = await userAPI.getUsers('distributor');
        setDistributors(distRes.data);
      }
    } catch (error) {
      toast.error('Failed to fetch users');
    } finally { setLoading(false); }
  };

  const fetchAllPackages = async () => {
    try {
      const r = await commissionPackagesAPI.list();
      setAllPackages(r.data || []);
    } catch {}
  };

  // Auto-generate username from name + phone
  const generateUsername = useCallback((name, phone) => {
    const base = (name || '').toLowerCase().replace(/[^a-z0-9]/g, '').substring(0, 8);
    const suffix = (phone || '').slice(-4);
    return base + suffix;
  }, []);

  // Auto-generate password
  const generatePassword = useCallback(() => {
    const chars = 'abcdefghijkmnpqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789';
    let pwd = '';
    for (let i = 0; i < 8; i++) pwd += chars[Math.floor(Math.random() * chars.length)];
    return pwd + Math.floor(10 + Math.random() * 90);
  }, []);

  // Auto-set username and password when name/phone changes
  const handleNewUserChange = (field, value) => {
    const updated = { ...newUser, [field]: value };
    if (field === 'name' || field === 'phone') {
      updated.email = updated.email || '';
      if (!newUser.password) {
        updated.password = generatePassword();
      }
    }
    setNewUser(updated);
  };

  // Pincode auto-fetch (browser-side)
  const handlePincodeLookup = async (pincode, isCreate = true) => {
    if (!pincode || pincode.length !== 6) return;
    setPinLoading(true);
    try {
      const r = await fetch(`https://api.postalpincode.in/pincode/${pincode}`);
      const data = await r.json();
      if (data?.[0]?.Status === 'Success' && data[0].PostOffice?.length) {
        const po = data[0].PostOffice[0];
        const city = po.District || '';
        const state = po.State || '';
        if (isCreate) {
          setNewUser(prev => ({ ...prev, city, state }));
        } else if (editUser) {
          setEditUser(prev => ({ ...prev, city, state }));
        }
        toast.success(`${city}, ${state}`);
      } else {
        toast.error('Invalid pincode');
      }
    } catch { toast.error('Pincode lookup failed'); }
    setPinLoading(false);
  };

  const handleCreateUser = async () => {
    if (!newUser.name || !newUser.phone || !newUser.role) {
      toast.error('Name, Phone and Role are required'); return;
    }
    if (newUser.role === 'retailer' && !newUser.parent_id) {
      toast.error('Please select a distributor'); return;
    }
    // Auto-generate email if empty
    const email = newUser.email || `${generateUsername(newUser.name, newUser.phone)}@tripathytelecom.shop`;
    const password = newUser.password || generatePassword();

    setCreateLoading(true);
    try {
      await userAPI.createUser({ ...newUser, email, password });
      toast.success('User created successfully');
      setCreateOpen(false);
      setNewUser({ name: '', email: '', phone: '', password: '', role: '', parent_id: '', shop_name: '', address: '', postal_code: '', city: '', state: '' });
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    } finally { setCreateLoading(false); }
  };

  const handleUpdateUser = async () => {
    if (!editUser) return;
    setEditLoading(true);
    try {
      const payload = {
        name: editUser.name, phone: editUser.phone, status: editUser.status,
        shop_name: editUser.shop_name, address: editUser.address,
        postal_code: editUser.postal_code, city: editUser.city, state: editUser.state,
        lock_balance: parseFloat(editUser.lock_balance) || 0,
        commission_package_id: editUser.commission_package_id || null,
      };
      if (editUser.new_password) payload.password = editUser.new_password;
      await userAPI.updateUser(editUser.id, payload);
      toast.success('User updated successfully');
      setEditOpen(false); setEditUser(null); fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user');
    } finally { setEditLoading(false); }
  };

  const handleDeleteUser = async (userId) => {
    if (!confirm('Are you sure you want to delete this user?')) return;
    try {
      await userAPI.deleteUser(userId);
      toast.success('User deleted'); fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const handleAddBalance = async () => {
    if (!addBalanceUser || !addBalanceAmount) return;
    const amount = parseFloat(addBalanceAmount);
    if (amount <= 0) { toast.error('Enter a valid amount'); return; }
    setAddBalanceLoading(true);
    try {
      await walletAPI.addBalance(addBalanceUser.id, amount);
      toast.success('Balance added');
      setAddBalanceOpen(false); setAddBalanceUser(null); setAddBalanceAmount(''); fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add balance');
    } finally { setAddBalanceLoading(false); }
  };

  const handleToggleStatus = async (user) => {
    const newStatus = user.status === 'active' ? 'frozen' : 'active';
    try {
      await userAPI.updateUser(user.id, { status: newStatus });
      toast.success(`User ${newStatus === 'active' ? 'activated' : 'frozen'}`); fetchUsers();
    } catch { toast.error('Failed to update status'); }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied!');
  };

  const filteredUsers = users.filter((u) =>
    u.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.phone?.includes(searchTerm) ||
    u.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.shop_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const roleBadge = (role) => {
    const map = {
      admin: 'bg-blue-50 text-[#4680ff] border-blue-100',
      distributor: 'bg-blue-50 text-blue-700 border-blue-100',
      retailer: 'bg-emerald-50 text-emerald-700 border-emerald-100',
      super_distributor: 'bg-violet-50 text-violet-700 border-violet-100',
      api_user: 'bg-orange-50 text-orange-700 border-orange-100',
    };
    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium border capitalize ${map[role] || 'bg-slate-50 text-slate-700 border-slate-100'}`}>
        {role?.replace('_', ' ')}
      </span>
    );
  };

  const statusBadge = (status) => (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium border ${
      status === 'active' ? 'bg-emerald-50 text-emerald-700 border-emerald-100' : 'bg-red-50 text-red-700 border-red-100'
    }`}>
      <span className={`w-1.5 h-1.5 rounded-full ${status === 'active' ? 'bg-emerald-500' : 'bg-red-500'}`} />
      {status === 'active' ? 'Active' : 'Frozen'}
    </span>
  );

  const isAdmin = currentUser?.role === 'admin';

  return (
    <DashboardLayout>
      <div className="space-y-5 animate-fade-in" data-testid="users-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">User Management</h1>
            <p className="text-sm text-slate-500 mt-0.5">Manage distributors and retailers</p>
          </div>
          <Dialog open={createOpen} onOpenChange={setCreateOpen}>
            <DialogTrigger asChild>
              <Button className="bg-[#4680ff] hover:bg-[#2563eb] text-white shadow-lg shadow-blue-500/20 rounded-lg h-10 px-5 text-sm font-semibold btn-press" data-testid="create-user-btn">
                <UserPlus className="w-4 h-4 mr-2" /> Add User
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Create New User</DialogTitle>
                <DialogDescription>Add a new distributor or retailer</DialogDescription>
              </DialogHeader>
              <div className="space-y-3 py-3">
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-[10px] font-semibold text-slate-500 uppercase">Full Name *</Label>
                    <Input placeholder="Enter name" value={newUser.name} onChange={(e) => handleNewUserChange('name', e.target.value)} className="h-9 rounded-lg text-sm" data-testid="create-user-name" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-[10px] font-semibold text-slate-500 uppercase">Mobile Number *</Label>
                    <Input placeholder="Enter phone" value={newUser.phone} onChange={(e) => handleNewUserChange('phone', e.target.value)} className="h-9 rounded-lg text-sm" data-testid="create-user-phone" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-[10px] font-semibold text-slate-500 uppercase">Email</Label>
                    <Input type="email" placeholder="Auto-generated if empty" value={newUser.email} onChange={(e) => setNewUser({ ...newUser, email: e.target.value })} className="h-9 rounded-lg text-sm" data-testid="create-user-email" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-[10px] font-semibold text-slate-500 uppercase">Password</Label>
                    <div className="flex gap-1">
                      <Input placeholder="Auto-generated" value={newUser.password} onChange={(e) => setNewUser({ ...newUser, password: e.target.value })} className="h-9 rounded-lg text-sm font-mono flex-1" data-testid="create-user-password" />
                      <Button variant="outline" size="sm" className="h-9 px-2" onClick={() => setNewUser({ ...newUser, password: generatePassword() })} title="Generate"><RefreshCw className="w-3.5 h-3.5" /></Button>
                    </div>
                  </div>
                </div>
                <div className="space-y-1">
                  <Label className="text-[10px] font-semibold text-slate-500 uppercase">Shop Name</Label>
                  <Input placeholder="Enter shop name" value={newUser.shop_name} onChange={(e) => setNewUser({ ...newUser, shop_name: e.target.value })} className="h-9 rounded-lg text-sm" data-testid="create-user-shop" />
                </div>
                <div className="space-y-1">
                  <Label className="text-[10px] font-semibold text-slate-500 uppercase">Address</Label>
                  <Input placeholder="Enter address" value={newUser.address} onChange={(e) => setNewUser({ ...newUser, address: e.target.value })} className="h-9 rounded-lg text-sm" data-testid="create-user-address" />
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div className="space-y-1">
                    <Label className="text-[10px] font-semibold text-slate-500 uppercase">Postal Code</Label>
                    <div className="flex gap-1">
                      <Input placeholder="6-digit" value={newUser.postal_code} onChange={(e) => {
                        const v = e.target.value.replace(/\D/g, '').substring(0, 6);
                        setNewUser({ ...newUser, postal_code: v });
                        if (v.length === 6) handlePincodeLookup(v, true);
                      }} className="h-9 rounded-lg text-sm font-mono" data-testid="create-user-pincode" />
                    </div>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-[10px] font-semibold text-slate-500 uppercase">City {pinLoading && <Loader2 className="w-3 h-3 inline animate-spin" />}</Label>
                    <Input value={newUser.city} onChange={(e) => setNewUser({ ...newUser, city: e.target.value })} className="h-9 rounded-lg text-sm" data-testid="create-user-city" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-[10px] font-semibold text-slate-500 uppercase">State</Label>
                    <Input value={newUser.state} onChange={(e) => setNewUser({ ...newUser, state: e.target.value })} className="h-9 rounded-lg text-sm" data-testid="create-user-state" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-[10px] font-semibold text-slate-500 uppercase">Role *</Label>
                    <Select value={newUser.role} onValueChange={(value) => setNewUser({ ...newUser, role: value, parent_id: '' })}>
                      <SelectTrigger className="h-9 rounded-lg" data-testid="create-user-role"><SelectValue placeholder="Select role" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="super_distributor">Super Distributor</SelectItem>
                        <SelectItem value="distributor">Distributor</SelectItem>
                        <SelectItem value="retailer">Retailer</SelectItem>
                        <SelectItem value="api_user">API User</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {newUser.role === 'retailer' && (
                    <div className="space-y-1">
                      <Label className="text-[10px] font-semibold text-slate-500 uppercase">Parent Distributor *</Label>
                      <Select value={newUser.parent_id} onValueChange={(value) => setNewUser({ ...newUser, parent_id: value })}>
                        <SelectTrigger className="h-9 rounded-lg" data-testid="create-user-parent"><SelectValue placeholder="Select distributor" /></SelectTrigger>
                        <SelectContent>
                          {distributors.map((d) => (<SelectItem key={d.id} value={d.id}>{d.name} ({d.phone})</SelectItem>))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setCreateOpen(false)} className="rounded-lg">Cancel</Button>
                <Button onClick={handleCreateUser} disabled={createLoading} className="bg-[#4680ff] hover:bg-[#2563eb] text-white rounded-lg" data-testid="create-user-submit">
                  {createLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create User'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input placeholder="Search by name, email, phone, username, shop..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10 h-10 bg-white border-slate-200 rounded-lg text-sm" data-testid="user-search-input" />
          </div>
          {isAdmin && (
            <Select value={roleFilter} onValueChange={setRoleFilter}>
              <SelectTrigger className="w-full sm:w-44 h-10 bg-white rounded-lg" data-testid="role-filter">
                <Filter className="w-3.5 h-3.5 mr-2 text-slate-400" /><SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Roles</SelectItem>
                <SelectItem value="super_distributor">Super Distributors</SelectItem>
                <SelectItem value="distributor">Distributors</SelectItem>
                <SelectItem value="retailer">Retailers</SelectItem>
                <SelectItem value="api_user">API Users</SelectItem>
              </SelectContent>
            </Select>
          )}
        </div>

        {/* Table */}
        <Card className="border border-slate-100 shadow-sm rounded-xl overflow-hidden">
          <div className="px-5 py-3 bg-slate-50/50 border-b border-slate-100 flex items-center justify-between">
            <p className="text-sm font-semibold text-slate-700">Users <span className="text-slate-400 font-normal">({filteredUsers.length})</span></p>
          </div>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-5 space-y-3">
                {[1, 2, 3, 4, 5].map((i) => (<Skeleton key={i} className="h-14 w-full rounded-lg" />))}
              </div>
            ) : filteredUsers.length === 0 ? (
              <div className="text-center py-16">
                <UsersIcon className="w-12 h-12 mx-auto mb-3 text-slate-200" />
                <p className="text-sm text-slate-400 font-medium">No users found</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50/30 hover:bg-slate-50/30">
                      <TableHead className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider pl-5">User</TableHead>
                      <TableHead className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Contact</TableHead>
                      <TableHead className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Role</TableHead>
                      <TableHead className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider text-right">Wallet</TableHead>
                      {isAdmin && <TableHead className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider text-right">Lock</TableHead>}
                      <TableHead className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Status</TableHead>
                      {isAdmin && <TableHead className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Package</TableHead>}
                      <TableHead className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider text-right pr-5">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredUsers.map((u) => (
                      <TableRow key={u.id} className="table-row-hover border-b border-slate-50">
                        <TableCell className="pl-5">
                          <p className="font-medium text-slate-800 text-sm">{u.name}</p>
                          {u.shop_name && <p className="text-[10px] text-slate-400 flex items-center gap-0.5"><Store className="w-3 h-3" />{u.shop_name}</p>}
                          {u.username && <p className="text-[10px] text-blue-500 font-mono">@{u.username}</p>}
                        </TableCell>
                        <TableCell>
                          <p className="text-sm text-slate-700">{u.email}</p>
                          <p className="text-xs text-slate-400 font-mono">{u.phone}</p>
                        </TableCell>
                        <TableCell>{roleBadge(u.role)}</TableCell>
                        <TableCell className="text-right font-mono text-sm font-medium text-slate-700">&#8377;{u.wallet_balance?.toLocaleString()}</TableCell>
                        {isAdmin && <TableCell className="text-right font-mono text-xs text-red-500">{u.lock_balance > 0 ? `₹${u.lock_balance?.toLocaleString()}` : '-'}</TableCell>}
                        <TableCell>{statusBadge(u.status)}</TableCell>
                        {isAdmin && <TableCell><span className="text-[11px] text-slate-500">{allPackages.find(p => p.id === u.commission_package_id)?.name || '-'}</span></TableCell>}
                        <TableCell className="text-right pr-5">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-8 w-8 text-slate-400 hover:text-slate-700" data-testid={`user-actions-${u.id}`}>
                                <MoreVertical className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-44">
                              {isAdmin && (
                                <DropdownMenuItem onClick={() => { setDetailUser(u); setDetailOpen(true); }}>
                                  <Eye className="w-3.5 h-3.5 mr-2" /> View Details
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuItem onClick={() => { setEditUser({ ...u, new_password: '' }); setEditOpen(true); }}>
                                <Edit className="w-3.5 h-3.5 mr-2" /> Edit
                              </DropdownMenuItem>
                              {isAdmin && (
                                <DropdownMenuItem onClick={() => { setAddBalanceUser(u); setAddBalanceOpen(true); }}>
                                  <Wallet className="w-3.5 h-3.5 mr-2" /> Add Balance
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuItem onClick={() => handleToggleStatus(u)}>
                                {u.status === 'active' ? <><Ban className="w-3.5 h-3.5 mr-2" /> Freeze</> : <><CheckCircle className="w-3.5 h-3.5 mr-2" /> Activate</>}
                              </DropdownMenuItem>
                              {isAdmin && (
                                <DropdownMenuItem className="text-destructive focus:text-destructive" onClick={() => handleDeleteUser(u.id)}>
                                  <Trash2 className="w-3.5 h-3.5 mr-2" /> Delete
                                </DropdownMenuItem>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* View Detail Dialog (Admin Only) */}
        <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>User Details</DialogTitle>
              <DialogDescription>{detailUser?.name} - Full Information</DialogDescription>
            </DialogHeader>
            {detailUser && (
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { label: 'Name', value: detailUser.name },
                    { label: 'Username', value: detailUser.username ? `@${detailUser.username}` : '-', mono: true },
                    { label: 'Email', value: detailUser.email },
                    { label: 'Phone', value: detailUser.phone, mono: true },
                    { label: 'Role', value: detailUser.role?.replace('_', ' '), upper: true },
                    { label: 'Status', value: detailUser.status, upper: true },
                    { label: 'Shop Name', value: detailUser.shop_name || '-' },
                    { label: 'Address', value: detailUser.address || '-' },
                    { label: 'Postal Code', value: detailUser.postal_code || '-', mono: true },
                    { label: 'City', value: detailUser.city || '-' },
                    { label: 'State', value: detailUser.state || '-' },
                    { label: 'Created', value: detailUser.created_at ? format(new Date(detailUser.created_at), 'dd MMM yyyy HH:mm') : '-' },
                  ].map(item => (
                    <div key={item.label} className="p-2 bg-slate-50 rounded-lg border border-slate-100">
                      <p className="text-[9px] font-semibold text-slate-400 uppercase">{item.label}</p>
                      <p className={`text-xs font-medium text-slate-800 ${item.mono ? 'font-mono' : ''} ${item.upper ? 'capitalize' : ''}`}>{item.value}</p>
                    </div>
                  ))}
                </div>
                {/* Password - Admin Only */}
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="text-[9px] font-semibold text-amber-600 uppercase mb-1">Password (Admin Only)</p>
                  <div className="flex items-center gap-2">
                    <code className="text-sm font-mono font-bold text-amber-800 bg-amber-100 px-2 py-1 rounded">{detailUser.plain_password || 'Not available'}</code>
                    {detailUser.plain_password && (
                      <Button variant="ghost" size="sm" className="h-7 px-2" onClick={() => copyToClipboard(detailUser.plain_password)}>
                        <Copy className="w-3.5 h-3.5" />
                      </Button>
                    )}
                  </div>
                </div>
                {/* Wallet Info */}
                <div className="grid grid-cols-2 gap-2">
                  <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                    <p className="text-[9px] font-semibold text-emerald-600 uppercase">Wallet Balance</p>
                    <p className="text-lg font-bold text-emerald-800 font-mono">&#8377;{detailUser.wallet_balance?.toLocaleString()}</p>
                  </div>
                  <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-[9px] font-semibold text-red-600 uppercase flex items-center gap-1"><Lock className="w-3 h-3" /> Lock Balance</p>
                    <p className="text-lg font-bold text-red-800 font-mono">&#8377;{detailUser.lock_balance?.toLocaleString() || '0'}</p>
                  </div>
                </div>
                <div className="p-2 bg-blue-50 border border-blue-200 rounded-lg text-[10px] text-blue-700">
                  Usable Balance: <strong className="font-mono">&#8377;{((detailUser.wallet_balance || 0) - (detailUser.lock_balance || 0)).toLocaleString()}</strong>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Edit Dialog */}
        <Dialog open={editOpen} onOpenChange={setEditOpen}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>Edit User</DialogTitle></DialogHeader>
            {editUser && (
              <div className="space-y-3 py-3">
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-[10px] font-semibold text-slate-500 uppercase">Name</Label>
                    <Input value={editUser.name} onChange={(e) => setEditUser({ ...editUser, name: e.target.value })} className="h-9 rounded-lg text-sm" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-[10px] font-semibold text-slate-500 uppercase">Phone</Label>
                    <Input value={editUser.phone} onChange={(e) => setEditUser({ ...editUser, phone: e.target.value })} className="h-9 rounded-lg text-sm" />
                  </div>
                </div>
                <div className="space-y-1">
                  <Label className="text-[10px] font-semibold text-slate-500 uppercase">Shop Name</Label>
                  <Input value={editUser.shop_name || ''} onChange={(e) => setEditUser({ ...editUser, shop_name: e.target.value })} className="h-9 rounded-lg text-sm" />
                </div>
                <div className="space-y-1">
                  <Label className="text-[10px] font-semibold text-slate-500 uppercase">Address</Label>
                  <Input value={editUser.address || ''} onChange={(e) => setEditUser({ ...editUser, address: e.target.value })} className="h-9 rounded-lg text-sm" />
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div className="space-y-1">
                    <Label className="text-[10px] font-semibold text-slate-500 uppercase">Postal Code</Label>
                    <Input value={editUser.postal_code || ''} onChange={(e) => {
                      const v = e.target.value.replace(/\D/g, '').substring(0, 6);
                      setEditUser({ ...editUser, postal_code: v });
                      if (v.length === 6) handlePincodeLookup(v, false);
                    }} className="h-9 rounded-lg text-sm font-mono" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-[10px] font-semibold text-slate-500 uppercase">City</Label>
                    <Input value={editUser.city || ''} onChange={(e) => setEditUser({ ...editUser, city: e.target.value })} className="h-9 rounded-lg text-sm" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-[10px] font-semibold text-slate-500 uppercase">State</Label>
                    <Input value={editUser.state || ''} onChange={(e) => setEditUser({ ...editUser, state: e.target.value })} className="h-9 rounded-lg text-sm" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-[10px] font-semibold text-slate-500 uppercase">Status</Label>
                    <Select value={editUser.status} onValueChange={(value) => setEditUser({ ...editUser, status: value })}>
                      <SelectTrigger className="h-9 rounded-lg"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="active">Active</SelectItem>
                        <SelectItem value="frozen">Frozen</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {isAdmin && (
                    <div className="space-y-1">
                      <Label className="text-[10px] font-semibold text-slate-500 uppercase flex items-center gap-1"><Lock className="w-3 h-3" /> Lock Balance (&#8377;)</Label>
                      <Input type="number" value={editUser.lock_balance || ''} onChange={(e) => setEditUser({ ...editUser, lock_balance: e.target.value })} className="h-9 rounded-lg text-sm font-mono" placeholder="0" />
                    </div>
                  )}
                </div>
                {/* Commission Package */}
                {isAdmin && editUser?.role !== 'admin' && (
                  <div className="space-y-1">
                    <Label className="text-[10px] font-semibold text-slate-500 uppercase flex items-center gap-1"><Package className="w-3 h-3" /> Commission Package</Label>
                    <Select value={editUser.commission_package_id || 'none'} onValueChange={(value) => setEditUser({ ...editUser, commission_package_id: value === 'none' ? '' : value })}>
                      <SelectTrigger className="h-9 rounded-lg" data-testid="edit-user-package"><SelectValue placeholder="No package" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No Package</SelectItem>
                        {allPackages.filter(p => {
                          if (editUser?.role === 'retailer') return p.user_type === 'retailer';
                          if (editUser?.role === 'distributor') return p.user_type === 'distributor';
                          if (editUser?.role === 'super_distributor') return p.user_type === 'super_distributor';
                          return true;
                        }).map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                {isAdmin && (
                  <div className="space-y-1">
                    <Label className="text-[10px] font-semibold text-slate-500 uppercase">New Password (leave empty to keep current)</Label>
                    <div className="flex gap-1">
                      <Input value={editUser.new_password || ''} onChange={(e) => setEditUser({ ...editUser, new_password: e.target.value })} className="h-9 rounded-lg text-sm font-mono flex-1" placeholder="Leave empty to keep current" />
                      <Button variant="outline" size="sm" className="h-9 px-2" onClick={() => setEditUser({ ...editUser, new_password: generatePassword() })}><RefreshCw className="w-3.5 h-3.5" /></Button>
                    </div>
                  </div>
                )}
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditOpen(false)} className="rounded-lg">Cancel</Button>
              <Button onClick={handleUpdateUser} disabled={editLoading} className="bg-[#4680ff] hover:bg-[#2563eb] text-white rounded-lg">
                {editLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Update'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Add Balance Dialog */}
        <Dialog open={addBalanceOpen} onOpenChange={setAddBalanceOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add Balance</DialogTitle>
              <DialogDescription>Add balance to {addBalanceUser?.name}'s wallet</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="p-4 bg-slate-50 rounded-xl border border-slate-100">
                <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Current Balance</p>
                <p className="text-2xl font-bold text-slate-900 font-mono mt-1">&#8377;{addBalanceUser?.wallet_balance?.toLocaleString()}</p>
                {addBalanceUser?.lock_balance > 0 && (
                  <p className="text-xs text-red-500 font-mono mt-1">Lock: &#8377;{addBalanceUser.lock_balance?.toLocaleString()}</p>
                )}
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Amount (&#8377;)</Label>
                <Input type="number" placeholder="Enter amount" value={addBalanceAmount} onChange={(e) => setAddBalanceAmount(e.target.value)} className="h-10 rounded-lg" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setAddBalanceOpen(false)} className="rounded-lg">Cancel</Button>
              <Button onClick={handleAddBalance} disabled={addBalanceLoading} className="bg-[#4680ff] hover:bg-[#2563eb] text-white rounded-lg">
                {addBalanceLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Add Balance'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default UsersPage;
